/**
 * A single event card within a day's schedule.
 */

import { useState } from 'react';
import {
  Pencil,
  X,
  Paperclip,
  FileText,
  Image,
  Film,
  Music,
  File,
  CheckCircle2,
  AlertCircle,
  Clock,
  Ban,
  HelpCircle,
  ExternalLink,
  Copy,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useTravelFeature } from '../hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';
import { toast } from '@/components/ui/use-toast';
import { captureError } from '@/lib/telemetry';
import api from '@/lib/api';
import type {
  TravelEventResponse,
  TravelEventType,
  TravelAttachmentResponse,
  TravelReservationStatus,
} from '../api';
import { getEventTypeColor, EVENT_TYPE_LABELS, sanitizeSourceUrl } from './TravelHelpers';
import { TravelAttachmentPreview } from './TravelAttachmentPreview';
import { TimePicker } from './TimePicker';
import { formatTime } from './TravelSettings';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return File;
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType.startsWith('video/')) return Film;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType.startsWith('text/') || mimeType.includes('pdf') || mimeType.includes('document'))
    return FileText;
  return File;
}

interface TravelEventCardProps {
  event: TravelEventResponse;
  attachments: TravelAttachmentResponse[];
  onUpdate: (eventId: string, input: Record<string, unknown>) => Promise<unknown>;
  onDelete: (eventId: string) => Promise<unknown>;
  onAddAttachment: (eventId: string, fileId: string) => Promise<unknown>;
  onRemoveAttachment: (attachmentId: string, documentId: string) => Promise<unknown>;
}

export function TravelEventCard({
  event,
  attachments,
  onUpdate,
  onDelete,
  onAddAttachment,
  onRemoveAttachment,
}: TravelEventCardProps) {
  const [editing, setEditing] = useState(false);
  const [time, setTime] = useState(event.time || '');
  const [title, setTitle] = useState(event.title);
  const [eventType, setEventType] = useState<TravelEventType>(event.eventType);
  const [notes, setNotes] = useState(event.notes || '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showFilePicker, setShowFilePicker] = useState(false);

  const color = getEventTypeColor(event.eventType);

  const handleSave = async () => {
    await onUpdate(event.eventId, {
      time: time || null,
      title,
      eventType,
      notes: notes || null,
    });
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setEditing(false);
      setTime(event.time || '');
      setTitle(event.title);
      setEventType(event.eventType);
      setNotes(event.notes || '');
    }
  };

  const handlePicked = async (file: VaultPickerResult) => {
    try {
      await onAddAttachment(event.eventId, file.documentId);
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        component: 'TravelEventCard',
        action: 'attach-file',
      });
      toast({
        title: 'Failed to attach file',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleDownload = async (att: TravelAttachmentResponse) => {
    const { url } = await api.getVaultDocumentDownloadUrl(att.fileId);
    window.open(url, '_blank');
  };

  // Shared attachment UI used in both editing and display modes
  const attachmentList = attachments.length > 0 && (
    <div className="space-y-1">
      {attachments.map((att) => {
        const Icon = getFileIcon(att.mimeType);
        return (
          <div
            key={att.attachmentId}
            className="group/att flex items-center gap-2 rounded border border-border px-2 py-1.5 text-sm hover:bg-accent/50 transition-colors"
          >
            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <button
              className="truncate text-left hover:underline flex-1 min-w-0"
              onClick={() => handleDownload(att)}
            >
              {att.fileName || att.fileId}
            </button>
            {att.fileSize != null && (
              <span className="text-xs text-muted-foreground shrink-0">
                {formatFileSize(att.fileSize)}
              </span>
            )}
            <button
              onClick={() => onRemoveAttachment(att.attachmentId, att.fileId)}
              className="p-0.5 rounded hover:bg-muted text-muted-foreground opacity-0 group-hover/att:opacity-100 transition-opacity shrink-0"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );

  const attachButtons = (
    <div className="flex items-center gap-1.5">
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={() => setShowFilePicker(true)}
      >
        <Paperclip className="h-3 w-3 mr-1" /> Attach file
      </Button>
    </div>
  );

  const fiasPickerDialog = (
    <VaultFilePickerDialog
      open={showFilePicker}
      onOpenChange={setShowFilePicker}
      title="Attach a File"
      description="Pick a file from your Vault, or upload a new one to attach."
      confirmLabel="Attach"
      onFileSelected={handlePicked}
    />
  );

  if (editing) {
    return (
      <div
        className="rounded-md border-2 border-blue-400/60 bg-card p-3 space-y-2"
        onKeyDown={handleKeyDown}
      >
        <div className="flex gap-2">
          <TimePicker value={time} onChange={setTime} placeholder="Time" className="w-32" />
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Event title"
            className="flex-1"
            autoFocus
          />
        </div>
        <div className="flex gap-2">
          <Select value={eventType} onValueChange={(v) => setEventType(v as TravelEventType)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(EVENT_TYPE_LABELS) as TravelEventType[]).map((type) => (
                <SelectItem key={type} value={type}>
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-2"
                    style={{ backgroundColor: getEventTypeColor(type) }}
                  />
                  {EVENT_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          rows={2}
        />
        {/* Attachments inside the editing form */}
        <div className="space-y-2 border-t border-border pt-2">
          <span className="text-xs font-medium text-muted-foreground">Attachments</span>
          {attachmentList}
          {attachButtons}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={!title.trim()}>
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
        {fiasPickerDialog}
      </div>
    );
  }

  return (
    <div className="group rounded-md border border-border bg-card p-2 hover:-translate-y-px hover:shadow-sm transition-all">
      <div className="flex items-start gap-2">
        <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {event.time && (
              <span className="text-xs font-medium text-muted-foreground shrink-0">
                {formatTime(event.time)}
              </span>
            )}
            {editingTitle ? (
              <Input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => {
                  const trimmed = titleDraft.trim();
                  if (trimmed && trimmed !== event.title)
                    onUpdate(event.eventId, { title: trimmed });
                  setEditingTitle(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const trimmed = titleDraft.trim();
                    if (trimmed && trimmed !== event.title)
                      onUpdate(event.eventId, { title: trimmed });
                    setEditingTitle(false);
                  }
                  if (e.key === 'Escape') setEditingTitle(false);
                  e.stopPropagation();
                }}
                className="h-5 text-sm font-medium py-0 px-1 border-none shadow-none focus-visible:ring-1"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                className="text-sm font-medium truncate cursor-text hover:opacity-70 transition-opacity"
                onClick={() => {
                  setTitleDraft(event.title);
                  setEditingTitle(true);
                }}
              >
                {event.title}
              </span>
            )}
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0"
              style={{
                backgroundColor: color + '20',
                color,
              }}
            >
              {EVENT_TYPE_LABELS[event.eventType]}
            </span>
            {attachments.length > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 shrink-0">
                <Paperclip className="h-2.5 w-2.5" /> {attachments.length}
              </span>
            )}
            <ReservationBadge event={event} />
          </div>
          {event.notes && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{event.notes}</p>
          )}
          {/* Attachment previews (images rendered inline, text shown, collapsible) */}
          <TravelAttachmentPreview attachments={attachments} />
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="p-1 rounded hover:bg-muted text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            onClick={() => onDelete(event.eventId)}
            className="p-1 rounded hover:bg-muted text-muted-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
      {fiasPickerDialog}
    </div>
  );
}

// ============================================================================
// Reservation Badge (Feature 9)
// ============================================================================

const STATUS_META: Record<
  TravelReservationStatus,
  { label: string; icon: typeof CheckCircle2; cls: string; tone: 'ok' | 'warn' | 'bad' | 'muted' }
> = {
  no_booking: {
    label: 'No booking',
    icon: Ban,
    cls: 'bg-muted text-muted-foreground',
    tone: 'muted',
  },
  suggested: {
    label: 'Book suggested',
    icon: Clock,
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    tone: 'warn',
  },
  required: {
    label: 'Book required',
    icon: AlertCircle,
    cls: 'bg-destructive/15 text-destructive',
    tone: 'bad',
  },
  booked: {
    label: 'Booked',
    icon: CheckCircle2,
    cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    tone: 'ok',
  },
  unknown: {
    label: 'Unknown',
    icon: HelpCircle,
    cls: 'bg-muted text-muted-foreground',
    tone: 'muted',
  },
};

function ReservationBadge({ event }: { event: TravelEventResponse }) {
  const feature = useTravelFeature('reservation_scout');
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!feature.enabled || event.eventType !== 'food') return null;
  const status = event.reservationStatus;
  if (!status) return null;
  const meta = STATUS_META[status];
  const Icon = meta.icon;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setSheetOpen(true);
        }}
        className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 ${meta.cls}`}
        aria-label="Open reservation details"
      >
        <Icon className="h-2.5 w-2.5" /> {meta.label}
      </button>
      <ReservationDetailsSheet event={event} open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}

function ReservationDetailsSheet({
  event,
  open,
  onOpenChange,
}: {
  event: TravelEventResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const status = event.reservationStatus;
  const notes = event.reservationNotes;

  const handleCopyDraft = () => {
    if (!notes?.draftEmail) return;
    void navigator.clipboard.writeText(notes.draftEmail).then(() => {
      toast({ title: 'Draft email copied to clipboard' });
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{event.title}</SheetTitle>
          <SheetDescription>Reservation details</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3 text-sm">
          {status && (
            <div>
              <span className="font-medium">Status:</span> {STATUS_META[status].label}
            </div>
          )}
          {notes?.bookAheadDays != null && (
            <div>
              <span className="font-medium">Book ahead:</span> {notes.bookAheadDays} day
              {notes.bookAheadDays === 1 ? '' : 's'}
            </div>
          )}
          {notes?.method && (
            <div>
              <span className="font-medium">Method:</span> {notes.method}
            </div>
          )}
          {(() => {
            const safeBookingUrl = sanitizeSourceUrl(notes?.url ?? null);
            if (!safeBookingUrl) return null;
            return (
              <div>
                <a
                  href={safeBookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline"
                >
                  Open booking page <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            );
          })()}
          {notes?.summary && (
            <div className="text-muted-foreground border-t border-border pt-3">{notes.summary}</div>
          )}
          {notes?.draftEmail && (
            <div className="border-t border-border pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-xs uppercase tracking-wide">Draft email</span>
                <Button size="sm" variant="ghost" onClick={handleCopyDraft}>
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
              <pre className="whitespace-pre-wrap text-xs bg-muted/30 rounded p-2 font-mono">
                {notes.draftEmail}
              </pre>
            </div>
          )}
          {(() => {
            // Drop any source whose URL doesn't survive client-side scheme
            // validation (defence-in-depth — server also sanitizes at persist).
            const safeSources = (notes?.sources ?? [])
              .map((s) => ({ safeUrl: sanitizeSourceUrl(s.url), title: s.title }))
              .filter(
                (s): s is { safeUrl: string; title: string | undefined } => s.safeUrl !== null,
              );
            if (safeSources.length === 0) return null;
            return (
              <div className="border-t border-border pt-3">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Sources
                </span>
                <ul className="space-y-1 mt-1">
                  {safeSources.map((s, i) => (
                    <li key={`${s.safeUrl}-${i}`}>
                      <a
                        href={s.safeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary underline truncate block"
                      >
                        {s.title || s.safeUrl}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>
      </SheetContent>
    </Sheet>
  );
}
